import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO_IMG =
  "https://images.unsplash.com/photo-1771790547217-8c70c9415956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBzaGFwZXMlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NzE0NTU3OXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name || u.email}`);
      nav(u.role === "admin" ? "/admin" : "/generator");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left visual panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-50 border-r border-zinc-200 relative overflow-hidden">
        <div className="relative z-10">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
            Marketing Image Generator
          </div>
          <h1 className="mt-4 font-heading text-5xl font-bold text-zinc-900 leading-tight">
            Production-grade<br />
            creatives. <span className="text-[#0055FF]">At ad scale.</span>
          </h1>
          <p className="mt-6 text-zinc-600 max-w-md leading-relaxed">
            Generate dozens of square marketing creatives from keywords in seconds.
            Built for media buyers running RSOC and search-arbitrage campaigns.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-3 max-w-md">
          {[
            ["1200×1200", "Square ad-ready"],
            ["10 styles", "Or bring your own"],
            ["~$0.04", "Per image"],
          ].map(([k, v]) => (
            <div key={k} className="border border-zinc-200 rounded-sm p-3 bg-white">
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-400">{v}</div>
              <div className="font-heading text-base font-semibold text-zinc-900 mt-1">{k}</div>
            </div>
          ))}
        </div>
        <img
          src={HERO_IMG}
          alt=""
          className="absolute -right-32 -bottom-24 w-[640px] opacity-25 grayscale pointer-events-none"
        />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <h2 className="font-heading text-3xl font-bold text-zinc-900">Sign in</h2>
            <p className="mt-2 text-sm text-zinc-500">
              Don't have an account?{" "}
              <Link to="/register" className="text-[#0055FF] font-medium hover:underline" data-testid="link-to-register">
                Request access
              </Link>
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-zinc-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="mt-1 rounded-sm border-zinc-200 focus-visible:ring-[#0055FF]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-zinc-700">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1 rounded-sm border-zinc-200 focus-visible:ring-[#0055FF]"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 font-medium"
            data-testid="login-submit-button"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>

          <div className="pt-4 border-t border-zinc-100 text-xs text-zinc-400 font-mono">
            New accounts require admin approval before sign-in.
          </div>
        </form>
      </div>
    </div>
  );
}
